'use client'

import { useRef } from "react"
import Image from "next/image"
import { usePathname } from "next/navigation"
import ProductCard, { CARD_ASPECT } from "../product/ProductCard"
import { Loader2, PackageOpen } from "lucide-react"
import { useInfiniteProducts } from "@/hooks/use-infinite-products"
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll"
import { useCountdown } from "@/hooks/use-countdown"
import { useProductsByIds, useCategoryExpander } from "@/lib/commerce/hooks"
import Skeleton from "@/components/elements/Skeleton"
import EmptyState from "../elements/EmptyState"
import { formatImageURLGoogle } from "@/utils/util"
import { navigateUrl } from "@/utils/client-request"
import { getCampaignDiscountInfo, getCampaignTargetInfo, getCampaignStatus } from "@/utils/campaign"
import moment from "moment"

const shimmer = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YzZjRmNiIvPjwvc3ZnPg=='
const myLoader = ({ src, width, quality }: any) => `${src}?w=${width}&q=${quality || 75}`

const focusRing = 'focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-foreground'
const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'
const gridClass = 'grid grid-cols-2 lg:grid-cols-4 gap-x-1 gap-y-8 lg:gap-y-12'
const STATUS_LABEL: Record<string, string> = { active: 'Идэвхтэй', expired: 'Дууссан', upcoming: 'Удахгүй' }

export default function Campaign({ vendor, campaign }: { vendor: any, campaign: any, wishlist?: any }) {
  const pathname = usePathname()
  const targetInfo = getCampaignTargetInfo(campaign.targets)
  const discountInfo = getCampaignDiscountInfo(campaign.targets)
  const status = getCampaignStatus(campaign.startDate, campaign.endDate)
  const countdown = useCountdown(campaign.endDate, status === 'active')

  return (
    <div className="w-full pb-16">
      {campaign.campaignBanner && (
        <div className="relative w-full aspect-16/9 sm:aspect-9/4 bg-muted overflow-hidden">
          <Image loader={myLoader} src={formatImageURLGoogle(campaign.campaignBanner)} alt={campaign.name} fill sizes="100vw" className="object-cover" placeholder="blur" blurDataURL={shimmer} priority />
        </div>
      )}

      <section className="px-4 lg:px-6 pt-8 lg:pt-12 pb-12 lg:pb-16 border-b border-border">
        <nav aria-label="Breadcrumb" className="flex items-center gap-x-1.5 text-xs text-muted-foreground">
          <a href={navigateUrl('/', pathname)} className={`transition-colors duration-150 hover:text-foreground ${focusRing}`}>Нүүр</a>
          <span aria-hidden>/</span>
          <span>Хямдралын аян</span>
        </nav>

        <div className="mt-6 lg:grid lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:gap-16">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase">
              {status === 'active' && <span aria-hidden className="size-1.5 rounded-full bg-foreground" />}
              <span className={status === 'active' ? 'text-foreground' : 'text-muted-foreground'}>{STATUS_LABEL[status] || STATUS_LABEL.upcoming}</span>
            </div>
            <h1 className="mt-4 text-3xl sm:text-5xl leading-tight tracking-tight text-foreground text-pretty">{campaign.name}</h1>
            {(discountInfo || campaign.type === 'freeShipping') && (
              <p className="mt-4 text-lg sm:text-xl text-foreground/80">
                {discountInfo && <span>{discountInfo.label} {discountInfo.suffix}</span>}
                {discountInfo && campaign.type === 'freeShipping' && <span className="text-muted-foreground"> · </span>}
                {campaign.type === 'freeShipping' && <span>Үнэгүй хүргэлт</span>}
              </p>
            )}
            {campaign.description && <p className="mt-4 max-w-2xl text-sm sm:text-base leading-relaxed text-muted-foreground">{campaign.description}</p>}
          </div>

          <div className="mt-10 lg:mt-0 flex flex-col gap-6 lg:items-end">
            {countdown ? (
              <Countdown countdown={countdown} />
            ) : campaign.startDate ? (
              <div className="lg:text-right">
                <div className={eyebrow}>Хугацаа</div>
                <div className="mt-2 text-lg tabular-nums">{moment(campaign.startDate).format('MM.DD')} – {campaign.endDate ? moment(campaign.endDate).format('MM.DD') : '∞'}</div>
              </div>
            ) : null}
            <a href="#products" className={`w-fit text-[13px] pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] bg-size-[100%_1px] hover:bg-size-[0%_1px] transition-[background-size] duration-500 ease-out motion-reduce:transition-none ${focusRing}`}>Бүтээгдэхүүн үзэх</a>
          </div>
        </div>
      </section>

      <div id="products" className="scroll-mt-20">
        {targetInfo.type === 'all' ? (
          <InfiniteCampaignGrid vendorId={vendor?.id} vendor={vendor} />
        ) : targetInfo.type === 'category' ? (
          <CategoryCampaignGrid vendorId={vendor?.id} categoryIds={targetInfo.categoryIds} vendor={vendor} />
        ) : targetInfo.type === 'product' ? (
          <ProductIdsCampaignGrid vendorId={vendor?.id} productIds={targetInfo.productIds} vendor={vendor} />
        ) : null}
      </div>
    </div>
  )
}

function pad2(n: number) { return String(n).padStart(2, '0') }

function Countdown({ countdown }: { countdown: { d: number, h: number, m: number, s: number } }) {
  const cells = [
    { v: countdown.d, k: 'өдөр' },
    { v: countdown.h, k: 'цаг' },
    { v: countdown.m, k: 'мин' },
    { v: countdown.s, k: 'сек' },
  ]
  return (
    <div className="lg:text-right">
      <div className={eyebrow}>Дуусахад</div>
      <div className="mt-3 flex items-start gap-4 sm:gap-6 lg:justify-end" role="timer" aria-label={`${countdown.d} өдөр ${countdown.h} цаг ${countdown.m} минут үлдсэн`}>
        {cells.map(c => (
          <div key={c.k} className="flex flex-col items-center">
            <span aria-hidden className="text-3xl sm:text-4xl font-light tabular-nums leading-none">{pad2(c.v)}</span>
            <span aria-hidden className="mt-2 text-[11px] tracking-[0.14em] uppercase text-muted-foreground">{c.k}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function CampaignProductGrid({ products, isLoading, isLoadingMore, total, hasMore, loadMore, vendor, skeletonCount }: { products: any[], isLoading: boolean, isLoadingMore?: boolean, total: number, hasMore?: boolean, loadMore?: () => void, vendor: any, skeletonCount: number }) {
  const loadMoreRef = useRef<HTMLDivElement>(null)
  useInfiniteScroll({
    targetRef: loadMoreRef,
    onLoadMore: () => loadMore?.(),
    enabled: !!hasMore && !!loadMore && !isLoading && !isLoadingMore,
  })

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })

  return (
    <section className="w-full px-4 lg:px-6 pt-12 lg:pt-16">
      <div className="flex items-end justify-between gap-6 mb-6 lg:mb-8">
        <h2 className="text-2xl sm:text-4xl tracking-tight text-foreground">Аянд хамрах бүтээгдэхүүн</h2>
        {!isLoading && <span className="shrink-0 text-[13px] text-muted-foreground tabular-nums">{total} бүтээгдэхүүн</span>}
      </div>
      {isLoading ? (
        <div className={gridClass}>
          {Array.from({ length: Math.min(skeletonCount, 8) || 4 }).map((_, index) => <SkeletonProduct key={index} />)}
        </div>
      ) : products.length > 0 ? (
        <>
          <div className={gridClass}>
            {products.map((product: any) => <ProductCard key={product.customId || product.id} product={product} vendor={vendor} />)}
          </div>
          {loadMore && (
            <div ref={loadMoreRef} className="w-full pt-12 flex justify-center">
              {isLoadingMore && (
                <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" strokeWidth={1.5} />
                  <span>Ачаалж байна...</span>
                </div>
              )}
              {!hasMore && products.length > 0 && (
                <div className="flex flex-col items-center gap-4 text-[13px]">
                  <span className="text-muted-foreground tabular-nums">Бүх бүтээгдэхүүн ачаалагдлаа ({total})</span>
                  <button type="button" onClick={scrollToTop} className={`h-10 px-6 border border-foreground tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background ${focusRing}`}>Дээш буцах</button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <EmptyState icon={PackageOpen} title="Бүтээгдэхүүн байхгүй байна" description="Энэ урамшуулалд хамаарах бүтээгдэхүүн байхгүй байна." />
      )}
    </section>
  )
}

function InfiniteCampaignGrid({ vendorId, vendor }: { vendorId: string, vendor: any }) {
  const { products, isLoading, isLoadingMore, metadata, loadMore, hasMore } = useInfiniteProducts({ vendorId, limit: 20 })
  return <CampaignProductGrid products={products} isLoading={isLoading} isLoadingMore={isLoadingMore} total={metadata.total} hasMore={hasMore} loadMore={loadMore} vendor={vendor} skeletonCount={8} />
}

function CategoryCampaignGrid({ vendorId, categoryIds, vendor }: { vendorId: string, categoryIds: string[], vendor: any }) {
  const { expandedIds, loaded } = useCategoryExpander(vendorId, categoryIds)
  if (!loaded) return <CampaignProductGrid products={[]} isLoading total={0} vendor={vendor} skeletonCount={8} />
  return <CategoryCampaignGridLoaded vendorId={vendorId} expandedIds={expandedIds} vendor={vendor} />
}

function CategoryCampaignGridLoaded({ vendorId, expandedIds, vendor }: { vendorId: string, expandedIds: string, vendor: any }) {
  const { products, isLoading, isLoadingMore, metadata, loadMore, hasMore } = useInfiniteProducts({ vendorId, limit: 20, initialFilters: { category: expandedIds } })
  return <CampaignProductGrid products={products} isLoading={isLoading} isLoadingMore={isLoadingMore} total={metadata.total} hasMore={hasMore} loadMore={loadMore} vendor={vendor} skeletonCount={8} />
}

function ProductIdsCampaignGrid({ vendorId, productIds, vendor }: { vendorId: string, productIds: string[], vendor: any }) {
  const { products, isLoading } = useProductsByIds(vendorId, productIds)
  return <CampaignProductGrid products={products} isLoading={isLoading} total={products.length} vendor={vendor} skeletonCount={productIds.length} />
}

function SkeletonProduct() {
  return (
    <div>
      <div className={`${CARD_ASPECT} w-full relative overflow-hidden`}>
        <Skeleton className="absolute inset-0 w-full h-full rounded-none" />
      </div>
      <Skeleton className="h-3.5 w-3/4 mt-3 rounded-none" />
      <Skeleton className="h-3.5 w-1/3 mt-2 rounded-none" />
    </div>
  )
}
